import { DutyOfDisclosure as AdsDutyOfDisclosure, FormBlock } from '@qbe/ads-blocks-forms';
import { Button, Typography } from '@qbe/ads-core';
import React from 'react';

import {
  useAppContent,
  useAppDispatch,
  useAppSelector,
  useBusinessDetails,
  useQuote,
} from '~/hooks';
import { updateDutyOfDisclosure } from '~/slices/businessDetails';
import { qalFormStartAction, qalTrackQuoteStartAction } from '~/thunks/analytics';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';
import { getReferralContent } from '~/utils/getReferralContent';

import { selectAppDetails, selectHasMultipleReferrals } from '../../selectors/selectAppDetails';

interface DutyOfDisclosureProps {
  onContinue?: () => void;
}

const DutyOfDisclosure: React.FC<DutyOfDisclosureProps> = ({ onContinue }) => {
  const dispatch = useAppDispatch();
  const { groups } = useAppSelector(selectAppDetails);
  const hasMoreThanOneReferral = useAppSelector(selectHasMultipleReferrals);

  // determine which referral content to show
  let referralContent: string | undefined;
  if (!hasMoreThanOneReferral) {
    referralContent = getReferralContent(groups);
  }

  const { dutyOfDisclosure } = useBusinessDetails();
  const { retrieveQuoteFlow } = useQuote();

  const { getTextContent: getTextSharedContent } = useAppContent({ basePath: 'shared' });
  const { getJSXContent } = useAppContent({ basePath: 'dsme.businessDetailsDutyOfDisclosure' });
  const { getJSXContent: getPartnerJSXContent } = useAppContent({
    basePath: 'dsme.businessDetailsPartnerDisclaimer',
  });
  /**
   * Use for google analytics Quote Start deduplication.
   * Using a flag for quoteStartId for deduplication.
   */
  const quoteStartEngagement = () => {
    if (!retrieveQuoteFlow) dispatch(qalTrackQuoteStartAction());
  };

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  const handleContinue = () => {
    dispatch(updateDutyOfDisclosure(true));
    handleFormStart();
    quoteStartEngagement();
    onContinue?.();
  };

  return (
    <>
      <AdsDutyOfDisclosure
        content={
          <>
            <Typography component="h1" gutterBottom fontWeight="fontWeightMedium">
              {getJSXContent('content.dutyOfDisclosure.title')}
            </Typography>
            <Typography component="div" variant="body2" gutterBottom>
              {getJSXContent('content.dutyOfDisclosure.description')}
            </Typography>

            <Typography variant="body1" gutterBottom fontWeight="fontWeightMedium">
              {getJSXContent('content.ifYouDontTellUsSomething.title')}
            </Typography>

            <Typography variant="body2" gutterBottom>
              {getJSXContent('content.ifYouDontTellUsSomething.description')}
            </Typography>
          </>
        }
        footer={
          <Typography variant="body3">
            {getJSXContent('content.footer.description')}
            {!hasMoreThanOneReferral && referralContent
              ? getPartnerJSXContent(`content.${referralContent}.description`)
              : null}
          </Typography>
        }
      />
      <FormBlock preset="button">
        <Button
          data-track="continue"
          label={getTextSharedContent('continueStepButttonLabel')}
          onClick={handleContinue}
          disabled={!!dutyOfDisclosure}
        />
      </FormBlock>
    </>
  );
};

export default DutyOfDisclosure;
